import React, { Reducer, useReducer, useState } from 'react'
import Image from 'next/image'
import { AuthorBadge } from '~pages/journal-page/author-badge'
import { JournalByTheme } from '~pages/journal-page/journal-by-theme'
import { getJournalData } from '~shared/api/single-journal-data/single-journal-data'
import { parseRichText } from '~shared/utils/parseRichText'
import { RelatedCourses } from '~pages/journal-page/journal-related-course'
import { FilesCollection } from '~widgets/lead-form/components/lead-form-pdf'

// Типы
type RichtextItem = {
  type:
    | 'text'
    | 'list'
    | 'header'
    | 'plus'
    | 'listDescription'
    | 'head'
    | 'stripe'
    | 'image'
  content: string
  keep: boolean
}

// Хелпер для scroll
const handleSmoothScroll = (e: React.MouseEvent, targetId: string) => {
  e.preventDefault()
  document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' })
}

// Хелпер для номера заголовка
const headerNumber = (name: string) => name?.split('#')?.[0]

// Конфиг заголовков h1-h5
const headersConfig = [
  {
    tag: 'h1',
    className:
      'text-[24px] md:text-[28px] 2lg:text-[32px] font-medium leading-[120%]'
  },
  {
    tag: 'h2',
    className:
      'text-[22px] md:text-[30px] 2lg:text-[36px] font-medium leading-[120%]'
  },
  {
    tag: 'h3',
    className:
      'text-[20px] md:text-[24px] 2lg:text-[26px] font-medium leading-[120%]'
  },
  {
    tag: 'h4',
    className: 'text-[18px] 2lg:text-[20px] font-medium leading-[120%] m-0'
  },
  {
    tag: 'h5',
    className: 'text-[18px] 2lg:text-[20px] font-medium leading-[120%] m-0'
  }
] as const

export const JournalCardBody = ({
  journal
}: {
  journal: Awaited<ReturnType<typeof getJournalData>>
}) => {
  const [active, setActive] = useState('')
  const [isSubjectTopics, toggleIsSubjectTopics] = useReducer<
    Reducer<boolean, void>
  >(prev => !prev, false)

  const parsedJournalContent = journal?.journalContent.map(content => ({
    ...content,
    description: parseRichText(content.description),
    advancedDescription: content.advancedDescription.map(advanced => ({
      ...advanced,
      name: parseRichText(advanced.name)
    })),
    advancedDescriptionBefore: content.advancedDescriptionBefore.map(
      advancedBefore => ({
        ...advancedBefore,
        name: parseRichText(advancedBefore.name)
      })
    )
  }))

  // Рендер richtext элементов
  const renderRichtext = (
    richtext: RichtextItem[],
    advancedDescriptionBefore: boolean
  ) => {
    let counter = 0
    return richtext.map(({ type, content, keep }, i) => {
      if (type === 'list') counter += 1
      else if (!(type === 'listDescription' || keep)) counter = 0

      switch (type) {
        case 'text':
          return (
            <div
              key={`text-${i}`}
              className='text-[16px] 2lg:text-[18px] [&_b]:font-medium [&_b]:text-[18px] 2lg:[&_b]:text-[20px]'
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )
        case 'header':
          return (
            <p
              key={`header-${i}`}
              className='text-[18px]'
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )
        case 'head':
          return (
            <span
              key={`head-${i}`}
              className='block my-[32px] text-[24px] 2lg:text-[36px] font-medium leading-[120%]'
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )
        case 'list':
          return (
            <div
              key={`list-${i}`}
              className='flex gap-[16px] mt-[24px] lg:mt-[32px]'>
              <div className='bg-[var(--beta)] min-w-[24px] w-[24px] h-[24px] min-h-[24px] rounded-full flex justify-center items-center text-[var(--alpha)] text-[14px]'>
                {counter}
              </div>
              <p
                className='text-[18px] md:text-[24px] font-medium'
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          )
        case 'listDescription':
          return (
            <p
              key={`listdesc-${i}`}
              className='ml-[40px] text-[18px] mt-[16px] lg:mt-[8px]'
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )
        case 'stripe':
          return (
            <div
              key={`stripe-${i}`}
              className='relative bg-[var(--stripe-bg,#F5F5F5)] rounded-[8px]'>
              <div className='border-l-[4px] border-[#3A52EE] rounded-[43px] absolute min-h-full max-h-fit' />
              <p
                className='p-[24px] text-[20px]'
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          )
        case 'plus':
          return (
            <div key={`plus-${i}`} className='flex mt-[16px] pl-[38px]'>
              <span className='w-[8px] min-w-[8px] h-[8px] border-[1.5px] border-[#3A52EE] rounded-full mr-[16px] mt-[6px]' />
              <p
                className='text-[18px]'
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          )
        case 'image':
          return (
            <div
              key={`image-${i}`}
              className='relative min-w-[-webkit-fill-available] h-[171px] md:h-[320px] 2lg:h-[400px]'>
              <Image
                fill
                sizes='(max-width: 768px) 75vw, 75vw'
                quality={75}
                src={content || '/'}
                alt={content?.match(/\/([^/]+)$/)?.[1] || ''}
                className='object-cover rounded-[16px]'
              />
            </div>
          )
        default:
          return null
      }
    })
  }

  return (
    <>
      {/* Содержание */}
      <div className='my-[56px]'>
        <div
          onClick={() => toggleIsSubjectTopics()}
          className={`flex flex-row items-center justify-between cursor-pointer pb-[16px]`}>
          <p className='text-[24px] font-medium'>Содержание</p>
          <svg
            className={`w-[16px] h-[16px] transition-transform ${isSubjectTopics ? 'rotate-180' : ''}`}
            viewBox='0 0 16 16'
            fill='none'>
            <path
              d='M4 6l4 4 4-4'
              stroke={isSubjectTopics ? '#3A52EE' : 'black'}
              strokeWidth='1.5'
              strokeLinecap='round'
            />
          </svg>
        </div>

        <div className='flex flex-col gap-[24px] mt-[16px]'>
          {isSubjectTopics &&
            journal?.journalContent?.map(
              (content, idx) =>
                content?.name?.includes('#') && (
                  <a
                    key={`subject-link-${idx}`}
                    href={`#journal-content__${idx + 1}`}
                    onClick={e =>
                      handleSmoothScroll(e, `journal-content__${idx + 1}`)
                    }
                    className='no-underline flex items-center gap-[16px] text-[20px]'>
                    <div
                      className='w-[8px] min-w-[8px] h-[8px] min-h-[8px] rounded-full border-[1.5px] border-[#3A52EE] transition-colors'
                      style={{
                        backgroundColor:
                          active === content.name ? '#3A52EE' : 'transparent'
                      }}
                    />
                    <p
                      onClick={() => setActive(`${content.name}`)}
                      className={`cursor-pointer text-[var(--omega)] transition-colors duration-300 hover:text-[var(--beta)] ${active === content.name ? 'underline decoration-black' : 'no-underline'}`}>
                      {String(content?.name?.replace(/.#/, ''))}
                    </p>
                  </a>
                )
            )}
        </div>
      </div>

      {/* Контент статьи */}
      {parsedJournalContent?.map(
        (content, idx) =>
          content && (
            <div
              key={`fragment-${idx}`}
              className='flex flex-col gap-[16px] md:gap-[24px] 2lg:gap-[32px] mt-[48px]'>
              {/* Заголовок секции */}
              {headersConfig.map((header, index) => {
                if (headerNumber(content.name ?? '') === `${index + 1}`) {
                  const Tag = header.tag
                  return (
                    <Tag
                      id={`journal-content__${idx + 1}`}
                      key={`header-${idx}-${index}`}
                      className={header.className}>
                      {content.name?.replace(/.#/, '') ?? ''}
                    </Tag>
                  )
                }
                return null
              })}

              {content.description && (
                <div>{renderRichtext(content.description, false)}</div>
              )}

              {content.quote && (
                <div className='mt-[-16px] md:mt-0'>
                  <AuthorBadge author={content.author} position='left' />
                </div>
              )}

              {Boolean(content?.advancedDescriptionBefore?.length) && (
                <div className='flex flex-col gap-[16px]'>
                  {content.advancedDescriptionBefore?.map(
                    (advanced, index) =>
                      (advanced.name || advanced.advancedTable) && (
                        <div
                          key={`advbefore-${index}`}
                          className='flex flex-col'>
                          {advanced.advancedTable && (
                            <div
                              className='my-[24px] [&>p]:text-[18px]'
                              dangerouslySetInnerHTML={{
                                __html: String(advanced.advancedTable)
                              }}
                            />
                          )}
                          {advanced.name && (
                            <div>{renderRichtext(advanced.name, true)}</div>
                          )}
                        </div>
                      )
                  )}
                </div>
              )}
              {journal?.byThemeWhere &&
                (idx + 1).toString() === journal?.byThemeWhere &&
                journal?.linkedJournal?.[0]?.journal?.name && (
                  <JournalByTheme linkedJournal={journal?.linkedJournal} />
                )}

              {(content.image?.url || content.title) && (
                <>
                  {content.image?.url && (
                    <div className='relative self-center min-w-[-webkit-fill-available] h-[171px] md:h-[320px] 2lg:h-[400px]'>
                      <Image
                        fill
                        sizes='(max-width: 768px) 50vw, 75vw'
                        quality={50}
                        alt=''
                        src={content.image?.url || '/'}
                        className='object-cover rounded-[16px]'
                      />
                    </div>
                  )}
                  {content.title && (
                    <p
                      className={`text-[12px] leading-[14px] ${content.image?.url ? 'mt-[16px]' : 'mt-0'}`}>
                      {content.title}
                    </p>
                  )}
                </>
              )}

              {content.advancedDescription?.map(
                (advanced, index) =>
                  advanced.name && (
                    <div key={`adv-${index}`}>
                      {renderRichtext(advanced.name, false)}
                    </div>
                  )
              )}

              {journal?.pdf &&
                journal?.pdfWhere &&
                (idx + 1).toString() === journal?.pdfWhere && (
                  <FilesCollection pdf={journal?.pdf} />
                )}
            </div>
          )
      )}
    </>
  )
}
